import React from "react";

// Trip-type presentation (values come from booking.tripType)
const TRIP_META = {
  oneway: { label: "One Way", color: "#0d6efd" },
  twoway: { label: "Two Way", color: "#fd7e14" },
  sharing: { label: "Sharing", color: "#20c997" },
};

export const TripTypeBadge = ({ tripType }) => {
  const meta = TRIP_META[tripType] || { label: tripType || "N/A", color: "#6c757d" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        background: meta.color,
        color: "#fff",
        borderRadius: "6px",
        padding: "2px 8px",
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
      }}
    >
      <i className="far fa-route"></i>
      {meta.label}
    </span>
  );
};

// distanceBreakdown.distance comes in two shapes across records:
//   number metres            e.g. 632771.938
//   { value: <metres|null> } e.g. { value: null, text: "NaN km" }
// Return metres as a number, or null when missing / unparseable.
const readDistanceMeters = (breakdown) => {
  const d = breakdown?.distance;
  if (d == null) return null;
  if (typeof d === "number") return Number.isNaN(d) ? null : d;
  if (typeof d === "object") {
    const v = Number(d.value);
    return d.value == null || Number.isNaN(v) ? null : v;
  }
  const n = Number(d);
  return Number.isNaN(n) ? null : n;
};

// The top-level Bookings.distanceBreakdown is sometimes null/NaN (esp. two-way),
// while the valid number lives on the BookingDetails row. Prefer the top-level
// value, fall back to BookingDetails[0].
const getDistanceMeters = (item) => {
  const top = readDistanceMeters(item?.distanceBreakdown);
  if (top != null) return top;
  return readDistanceMeters(item?.BookingDetails?.[0]?.distanceBreakdown);
};

// Two-way (round trip) covers the distance twice, matching the customer app
// which doubles KM and fare for tripType === 'twoway'.
const tripMultiplier = (item) => (item?.tripType === "twoway" ? 2 : 1);

// KM. Prefers the chargeable km frozen at booking (two-way per-day min logic).
export const bookingKm = (item) => {
  if (item?.chargeableKm != null) return Number(item.chargeableKm).toFixed(0);
  const m = getDistanceMeters(item);
  return m == null ? null : ((m / 1000) * tripMultiplier(item)).toFixed(0);
};

// Fare. Prefers the fare frozen at booking; falls back to the old ×2 formula.
export const bookingCost = (item) => {
  if (item?.estimatedFare != null) return Number(item.estimatedFare).toFixed(0);
  const m = getDistanceMeters(item);
  if (m == null) return null;
  const perKmRaw = Number(item?.VendorCar?.perKmCharge);
  if (Number.isNaN(perKmRaw)) return null;
  const perKm = item?.isAcAvailable ? perKmRaw + 1 : perKmRaw;
  return ((m / 1000) * perKm * tripMultiplier(item)).toFixed(0);
};

export const KmBadge = ({ item }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      background: "#eef1f6",
      color: "#212529",
      borderRadius: "6px",
      padding: "2px 8px",
      fontSize: "0.75rem",
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    <i className="far fa-road"></i>
    {bookingKm(item) == null ? "— KM" : `${bookingKm(item)} KM`}
  </span>
);

export const CostHighlight = ({ item }) => {
  const cost = bookingCost(item);
  return (
    <strong style={{ color: "#198754", fontSize: "1rem", fontWeight: 700, whiteSpace: "nowrap" }}>
      {cost == null ? "Rs. —" : `Rs. ${cost}`}
    </strong>
  );
};
